import { AdvancedDynamicTexture } from "@babylonjs/gui/2D/advancedDynamicTexture";
import { Rectangle } from "@babylonjs/gui/2D/controls/rectangle";
import { TextBlock } from "@babylonjs/gui/2D/controls/textBlock";
import { Button } from "@babylonjs/gui/2D/controls/button";
import { StackPanel } from "@babylonjs/gui/2D/controls/stackPanel";
import { Control } from "@babylonjs/gui/2D/controls/control";
import { t } from "./i18n";
import type { LocText } from "./schema";

export type Difficulty = "D" | "A" | "E";
const OCEAN = "#0e2233";
const GOLD = "#d8b24a";

/** In-game HUD: objectives, difficulty, tips, key reward, fullscreen/mute. */
export class Hud {
  readonly adt: AdvancedDynamicTexture;
  private objectiveText: TextBlock;
  private titleText: TextBlock;
  private tipBox: Rectangle;
  private tipText: TextBlock;
  private tipTimer: ReturnType<typeof setTimeout> | null = null;
  /** v9 multiplayer — "N pèlerins connectés" badge (hidden in solo). */
  private peersBox: Rectangle;
  private peersText: TextBlock;

  onFullscreen?: () => void;
  onMute?: () => boolean;
  onDifficulty?: (d: Difficulty) => void;
  onExit?: () => void;

  constructor() {
    this.adt = AdvancedDynamicTexture.CreateFullscreenUI("kq-ui");
    this.adt.idealWidth = 1280;
    this.adt.useSmallestIdeal = true;

    // Top bar
    const top = new Rectangle("topbar");
    top.height = "56px";
    top.width = 1;
    top.background = "rgba(6,16,24,0.6)";
    top.thickness = 0;
    top.verticalAlignment = Control.VERTICAL_ALIGNMENT_TOP;
    this.adt.addControl(top);

    this.titleText = new TextBlock("title", "");
    this.titleText.color = GOLD;
    this.titleText.fontSize = 20;
    this.titleText.paddingLeft = "18px";
    this.titleText.textHorizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
    top.addControl(this.titleText);

    const btnRow = new StackPanel();
    btnRow.isVertical = false;
    btnRow.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_RIGHT;
    btnRow.height = "44px";
    btnRow.paddingRight = "12px";
    top.addControl(btnRow);

    btnRow.addControl(this.iconBtn("⤢", () => this.onFullscreen?.()));
    const muteBtn = this.iconBtn("♪", () => {
      const muted = this.onMute?.();
      (muteBtn.children[0] as TextBlock).text = muted ? "🔇" : "♪";
    });
    btnRow.addControl(muteBtn);
    btnRow.addControl(this.iconBtn("⌂", () => this.onExit?.()));

    // Objective banner
    const objBar = new Rectangle("objbar");
    objBar.height = "40px";
    objBar.width = "0.7";
    objBar.background = "rgba(6,16,24,0.55)";
    objBar.cornerRadius = 12;
    objBar.thickness = 1;
    objBar.color = GOLD;
    objBar.verticalAlignment = Control.VERTICAL_ALIGNMENT_TOP;
    objBar.top = "66px";
    this.adt.addControl(objBar);
    this.objectiveText = new TextBlock("obj", "");
    this.objectiveText.color = "#f2ede0";
    this.objectiveText.fontSize = 16;
    objBar.addControl(this.objectiveText);

    // Difficulty selector (bottom-left)
    const diff = new StackPanel();
    diff.isVertical = false;
    diff.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
    diff.verticalAlignment = Control.VERTICAL_ALIGNMENT_BOTTOM;
    diff.height = "48px";
    diff.paddingLeft = "12px";
    diff.paddingBottom = "12px";
    this.adt.addControl(diff);
    (["D", "A", "E"] as Difficulty[]).forEach((d) => {
      const labels: Record<Difficulty, string> = { D: "Débutant", A: "Avancé", E: "Expert" };
      const b = Button.CreateSimpleButton("d" + d, labels[d]);
      b.width = "92px";
      b.height = "34px";
      b.color = "#f2ede0";
      b.background = d === "D" ? "#2c6b3f" : "#163247";
      b.cornerRadius = 8;
      b.paddingRight = "6px";
      b.fontSize = 13;
      b.onPointerUpObservable.add(() => {
        diff.children.forEach((c) => ((c as Button).background = "#163247"));
        b.background = "#2c6b3f";
        this.onDifficulty?.(d);
      });
      diff.addControl(b);
    });

    // v9 multiplayer — peers badge (top-right, under the top bar)
    this.peersBox = new Rectangle("peers");
    this.peersBox.height = "34px";
    this.peersBox.width = "300px";
    this.peersBox.cornerRadius = 10;
    this.peersBox.thickness = 1;
    this.peersBox.color = GOLD;
    this.peersBox.background = "rgba(6,16,24,0.55)";
    this.peersBox.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_RIGHT;
    this.peersBox.verticalAlignment = Control.VERTICAL_ALIGNMENT_TOP;
    this.peersBox.top = "66px";
    this.peersBox.paddingRight = "12px";
    this.peersBox.isVisible = false;
    this.adt.addControl(this.peersBox);
    this.peersText = new TextBlock("peerstext", "");
    this.peersText.color = "#a9d6c0";
    this.peersText.fontSize = 14;
    this.peersBox.addControl(this.peersText);

    // Tip toast (bottom-center)
    this.tipBox = new Rectangle("tip");
    this.tipBox.height = "44px";
    this.tipBox.width = "520px";
    this.tipBox.cornerRadius = 12;
    this.tipBox.background = "rgba(12,28,40,0.9)";
    this.tipBox.color = GOLD;
    this.tipBox.thickness = 1;
    this.tipBox.verticalAlignment = Control.VERTICAL_ALIGNMENT_BOTTOM;
    this.tipBox.paddingBottom = "68px";
    this.tipBox.isVisible = false;
    this.adt.addControl(this.tipBox);
    this.tipText = new TextBlock("tiptext", "");
    this.tipText.color = "#f2ede0";
    this.tipText.fontSize = 15;
    this.tipText.textWrapping = true;
    this.tipBox.addControl(this.tipText);
  }

  private iconBtn(glyph: string, cb: () => void): Button {
    const b = Button.CreateSimpleButton("ic" + glyph, glyph);
    b.width = "40px";
    b.height = "40px";
    b.color = GOLD;
    b.background = "rgba(20,50,71,0.9)";
    b.cornerRadius = 10;
    b.paddingLeft = "6px";
    b.fontSize = 18;
    b.onPointerUpObservable.add(cb);
    return b;
  }

  setTitle(title: string): void {
    this.titleText.text = title;
  }

  setObjective(text: string): void {
    this.objectiveText.text = text;
  }

  /** v9 multiplayer — live "N pèlerins connectés" badge + session code. */
  setPeers(count: number, code: string, connected: boolean): void {
    this.peersBox.isVisible = true;
    const n = count + 1; // self included — "pilgrims connected" counts everyone
    const who = t(
      n > 1
        ? { fr: `${n} pèlerins connectés`, en: `${n} pilgrims connected` }
        : { fr: "1 pèlerin connecté", en: "1 pilgrim connected" },
    );
    const link = connected ? "👥" : "⚠️";
    this.peersText.text = `${link} ${who} · ${code}`;
    this.peersText.color = connected ? "#a9d6c0" : "#e0a35a";
  }

  showTip(tip: LocText): void {
    this.tipText.text = "💡 " + t(tip);
    this.tipBox.isVisible = true;
    if (this.tipTimer) clearTimeout(this.tipTimer);
    this.tipTimer = setTimeout(() => (this.tipBox.isVisible = false), 5200);
  }

  /** Grace-framed reward: award the "Clé du Royaume". */
  showKeyReward(keyName: string, lesson: string, onNext: () => void): void {
    const scrim = new Rectangle("rewardScrim");
    scrim.background = "rgba(3,8,14,0.8)";
    scrim.thickness = 0;
    scrim.zIndex = 60;
    this.adt.addControl(scrim);

    const card = new Rectangle("rewardCard");
    card.width = "520px";
    card.adaptHeightToChildren = true;
    card.cornerRadius = 18;
    card.thickness = 2;
    card.color = GOLD;
    card.background = OCEAN;
    scrim.addControl(card);

    const stack = new StackPanel();
    stack.paddingLeft = "26px";
    stack.paddingRight = "26px";
    stack.paddingTop = "24px";
    stack.paddingBottom = "24px";
    card.addControl(stack);

    const glyph = new TextBlock("k", "🗝️");
    glyph.fontSize = 64;
    glyph.height = "84px";
    stack.addControl(glyph);

    const won = new TextBlock("won", t({ fr: "Tu as reçu une Clé du Royaume !", en: "You received a Key of the Kingdom!" }));
    won.color = GOLD;
    won.fontSize = 24;
    won.height = "40px";
    won.textWrapping = true;
    won.resizeToFit = true;
    stack.addControl(won);

    const kn = new TextBlock("kn", "✦ " + keyName);
    kn.color = "#f2ede0";
    kn.fontSize = 19;
    kn.height = "34px";
    stack.addControl(kn);

    const les = new TextBlock("les", les0(lesson));
    les.color = "#a9d6c0";
    les.fontSize = 16;
    les.textWrapping = true;
    les.resizeToFit = true;
    les.paddingTop = "10px";
    les.paddingBottom = "14px";
    stack.addControl(les);

    const cont = Button.CreateSimpleButton("cont", t({ fr: "Retour à La Cité", en: "Back to The City" }));
    cont.height = "44px";
    cont.color = OCEAN;
    cont.background = GOLD;
    cont.cornerRadius = 10;
    cont.onPointerUpObservable.add(() => {
      this.adt.removeControl(scrim);
      onNext();
    });
    stack.addControl(cont);
  }

  dispose(): void {
    if (this.tipTimer) clearTimeout(this.tipTimer);
    this.adt.dispose();
  }
}

function les0(s: string): string {
  return s;
}
